import type { NormalizedError } from '../api/errors';

/** HTML-escape a value for safe interpolation into template strings. */
export function esc(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function shortAddr(addr: string | null | undefined): string {
  if (!addr) return '—';
  return addr.length > 12 ? `${addr.slice(0, 4)}…${addr.slice(-4)}` : addr;
}

export interface BtnOpts {
  disabled?: boolean;
  variant?: 'primary' | 'danger' | 'ghost';
  title?: string;
}

/** A data-attribute-driven button; clicks are handled by the delegated root listener. */
export function btn(label: string, act: string, data: Record<string, string> = {}, opts: BtnOpts = {}): string {
  const attrs = Object.entries(data)
    .map(([k, v]) => `data-${esc(k)}="${esc(v)}"`)
    .join(' ');
  const cls = `btn${opts.variant ? ` btn--${opts.variant}` : ''}`;
  const title = opts.title ? `title="${esc(opts.title)}"` : '';
  const disabled = opts.disabled ? 'disabled aria-disabled="true"' : '';
  return `<button type="button" class="${cls}" data-ui-control="button" data-act="${esc(act)}" ${attrs} ${disabled} ${title}>${esc(label)}</button>`;
}

export function pill(text: string, kind: 'ok' | 'warn' | 'down' | 'muted' = 'muted'): string {
  return `<span class="pill pill--${kind}" data-status-kind="${kind}">${esc(text)}</span>`;
}

export function row(label: string, value: string): string {
  return `<div class="kv"><dt>${esc(label)}</dt><dd>${value}</dd></div>`;
}

export function errorBanner(error: NormalizedError | null): string {
  if (!error) return '';
  const detail = error.details ? ` <code>${esc(JSON.stringify(error.details))}</code>` : '';
  const req = error.requestId ? ` · req ${esc(error.requestId)}` : '';
  return `
    <div class="banner banner--error" role="alert" data-banner="error">
      <div>
        <strong>${esc(error.code)}</strong> (${esc(error.status)}) — ${esc(error.message)}${detail}${req}
      </div>
      ${btn('dismiss', 'dismiss-error', {}, { variant: 'ghost' })}
    </div>`;
}

export function noticeBanner(notice: string | null): string {
  if (!notice) return '';
  return `<div class="banner banner--notice" role="status" data-banner="notice">${esc(notice)}</div>`;
}

export function busyBar(busy: string | null): string {
  if (!busy) return '';
  return `<div class="busy" aria-live="polite">⏳ ${esc(busy)}…</div>`;
}
